import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import {
  changePayrollModel,
  changeUserRole,
  getEmployee,
  listEmployees,
  patchEmployee,
  provisionEmployeeUser,
  terminateEmployee,
} from '../api/employees.js';
import { useAuth } from '../auth/AuthContext.js';
import { createCompensation, listCompensations } from '../api/compensations.js';
import { listEmployeeAssets } from '../api/assets.js';
import { fmtAssignedAt } from './assetShared.js';
import {
  createPayComponent,
  endPayComponent,
  listPayComponents,
} from '../api/payComponents.js';
import {
  buildDocumentDownloadUrl,
  deleteEmployeeDocument,
  listEmployeeDocuments,
  uploadEmployeeDocument,
} from '../api/documents.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatBytes, formatMoney, formatDate } from '../lib/format.js';
import { DOCUMENT_KIND_OPTIONS } from '../lib/documents.js';
import type {
  CompensationResponse,
  CurrencyCode,
  EmployeeDocumentKind,
  EmployeeDocumentResponse,
  EmployeeResponse,
  NextOfKin,
  PayComponentResponse,
  ProvisionableUserRole,
  TerminateEmployeeResult,
  TerminationReason,
  UpdateEmployeeInput,
} from '@kora/types';
import {
  allowedCurrenciesForCountry,
  EMPLOYEE_DOCUMENT_ALLOWED_TYPES,
  EMPLOYEE_DOCUMENT_MAX_BYTES,
  socialSecurityLabelForCountry,
  taxIdLabelForCountry,
} from '@kora/types';

/** Display labels for roles. `owner` is shown but never assignable here. */
const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  hr: 'HR',
  finance: 'Finance',
  employee: 'Employee',
  it: 'IT (asset custodian)',
};

/** Roles an owner/admin may provision or assign (never `owner`). */
const PROVISIONABLE_ROLES: ReadonlyArray<{ value: ProvisionableUserRole; label: string }> = [
  { value: 'hr', label: 'HR' },
  { value: 'finance', label: 'Finance' },
  { value: 'admin', label: 'Admin' },
  { value: 'employee', label: 'Employee' },
  { value: 'it', label: 'IT (asset custodian)' },
];

/**
 * /employees/:id — detail view + compensation management.
 *
 * Day 19 addition: the compensation section lets operators set + view
 * salary history. Without this the app can only run payroll for
 * employees seeded via SQL, which is a non-starter for real use.
 */

export const employeeDetailRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/employees/$id',
  component: EmployeeDetailPage,
});

function EmployeeDetailPage(): JSX.Element {
  const { id } = employeeDetailRoute.useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['employees', 'detail', id],
    queryFn: () => getEmployee(id),
  });

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <nav className="text-xs text-gray-500">
        <Link to="/employees" className="hover:underline">
          Employees
        </Link>{' '}
        / {data ? `${data.firstName} ${data.lastName}` : id}
      </nav>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState error={error} /> : null}
      {data ? (
        <>
          <EmployeeSection employee={data} />
          <CompensationSection employee={data} />
          <PayComponentSection employee={data} />
          <DocumentsSection employee={data} />
          <AssetsHeldSection employeeId={data.id} />
          <TerminationSection employee={data} />
        </>
      ) : null}
    </section>
  );
}

/**
 * EmployeeSection — read view + inline edit toggle.
 *
 * Why a section wrapper instead of a modal: there are 11 editable
 * fields across personal details + identifiers + emergency contact.
 * A modal at the volumes operators need (HR onboarding 50 employees
 * a day) would scroll-jail the form. Inline keeps everything in
 * context with the rest of the employee record.
 *
 * Edit-mode disabled for terminated employees — the server rejects
 * PATCH on terminated rows with 409 anyway, but we hide the entry
 * point so operators don't try.
 */
function EmployeeSection({ employee }: { employee: EmployeeResponse }): JSX.Element {
  // All hooks declared at the top — Rules of Hooks. The early return
  // for edit mode below would otherwise short-circuit subsequent
  // hook calls on the read-mode render path.
  const { user: me } = useAuth();
  const [editing, setEditing] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const canEdit = employee.status !== 'terminated';
  const linkedUser = employee.user ?? null;
  // Role assignment / change is an identity-management concern: owner + admin.
  const canManageRoles = me?.role === 'owner' || me?.role === 'admin';

  if (editing && canEdit) {
    return (
      <EmployeeEditForm
        employee={employee}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    );
  }

  return (
    <>
      <EmployeeCard
        employee={employee}
        linkedUser={linkedUser}
        onEdit={canEdit ? () => setEditing(true) : null}
        // Provision is offered when there's no login yet, OR when a login
        // exists but is still pending acceptance (status 'invited'). The
        // latter is the re-invite case: an employee uploaded with the wrong
        // email whose first invite bounced. The same modal resends — it
        // corrects the login email, supersedes the dead token, and mints a
        // fresh link (backend POST /v1/employees/:id/provision-user already
        // treats a repeat call on an 'invited' user as a resend). Once the
        // login is active, the action becomes "Change role" (owner/admin only).
        onProvisionLogin={
          canEdit && (!linkedUser || linkedUser.status === 'invited')
            ? () => setProvisionOpen(true)
            : null
        }
        onChangeRole={linkedUser && canManageRoles ? () => setRoleOpen(true) : null}
        // ADR 0010 Phase 4 — changing payroll model is owner/admin only,
        // available regardless of whether a login exists.
        onChangePayrollModel={canManageRoles ? () => setPayrollOpen(true) : null}
      />
      {provisionOpen ? (
        <ProvisionLoginModal
          employee={employee}
          onClose={() => setProvisionOpen(false)}
        />
      ) : null}
      {roleOpen && linkedUser ? (
        <RoleChangeModal
          employee={employee}
          currentRole={linkedUser.role}
          onClose={() => setRoleOpen(false)}
        />
      ) : null}
      {payrollOpen ? (
        <PayrollModelChangeModal
          employee={employee}
          onClose={() => setPayrollOpen(false)}
        />
      ) : null}
    </>
  );
}

/** ADR 0010 — informational badge shown only for international_remote. */
function PayrollModelBadge({ employee }: { employee: EmployeeResponse }): JSX.Element | null {
  if (employee.payrollModel !== 'international_remote') return null;
  return (
    <span className="text-xs px-2 py-0.5 rounded border bg-blue-950 text-blue-300 border-blue-900">
      International remote
    </span>
  );
}

function EmployeeCard({
  employee: e,
  linkedUser,
  onEdit,
  onProvisionLogin,
  onChangeRole,
  onChangePayrollModel,
}: {
  employee: EmployeeResponse;
  linkedUser: NonNullable<EmployeeResponse['user']> | null;
  onEdit: (() => void) | null;
  onProvisionLogin: (() => void) | null;
  onChangeRole: (() => void) | null;
  onChangePayrollModel: (() => void) | null;
}): JSX.Element {
  return (
    <article className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl">
              {e.firstName}
              {e.middleName ? ` ${e.middleName}` : ''} {e.lastName}
            </h1>
            <PayrollModelBadge employee={e} />
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span className="font-mono text-xs">{e.employeeNumber}</span>
            {e.position ? (
              <>
                <span>·</span>
                <span>{e.position}</span>
              </>
            ) : null}
            {e.department ? (
              <>
                <span>·</span>
                <span className="text-gray-500">{e.department}</span>
              </>
            ) : null}
            <span>·</span>
            <StatusPill status={e.status} />
            {linkedUser ? (
              <>
                <span>·</span>
                <span className="text-gray-400">
                  Login: {ROLE_LABELS[linkedUser.role] ?? linkedUser.role}
                  {linkedUser.status === 'invited' ? ' (invited)' : ''}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onProvisionLogin && e.status !== 'terminated' ? (
            <button
              type="button"
              onClick={onProvisionLogin}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              {linkedUser?.status === 'invited' ? 'Resend invitation' : 'Provision login'}
            </button>
          ) : null}
          {onChangeRole ? (
            <button
              type="button"
              onClick={onChangeRole}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              Change role
            </button>
          ) : null}
          {onChangePayrollModel ? (
            <button
              type="button"
              onClick={onChangePayrollModel}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              Change payroll model
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              onClick={onEdit}
              className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
            >
              Edit
            </button>
          ) : null}
          {/* Loan-migration: direct entry point to per-employee loan admin. */}
          <Link
            to="/employees/$id/advances"
            params={{ id: e.id }}
            search={{ mode: 'new' as const }}
            className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
          >
            Manage loans
          </Link>
        </div>
      </header>

      <div className="rounded border border-gray-900 divide-y divide-gray-900">
        <Row label="Country" value={`${e.countryCode}${e.stateCode ? ` · ${e.stateCode}` : ''}`} />
        <Row label="Hired" value={formatDate(e.hireDate)} />
        <Row label="Date of birth" value={e.dateOfBirth ? formatDate(e.dateOfBirth) : '—'} />
        <Row label="Gender" value={e.gender ? formatGender(e.gender) : '—'} />
        <Row label="Email" value={e.email ?? '—'} />
        <Row label="Phone" value={e.phone ?? '—'} />
        <Row label="Home address" value={e.homeAddress ?? '—'} />
        <Row label="Department" value={e.department ?? '—'} />
        <Row label="Position" value={e.position ?? '—'} />
        <Row
          label="Work arrangement"
          value={e.employmentType ? formatEmploymentType(e.employmentType) : '—'}
        />
        <Row label="Work location" value={e.workLocation ?? '—'} />
        <Row
          label="Manager"
          value={e.manager ? `${e.manager.firstName} ${e.manager.lastName}` : '—'}
        />
        <Row label="SWIFT/BIC" value={e.bankSwiftCode ?? '—'} mono />
        {e.bankName ? <Row label="Bank" value={e.bankName} /> : null}
        {e.bankAccountNumber ? <Row label="Account No." value={e.bankAccountNumber} mono /> : null}
        {e.bankAccountName ? <Row label="Account Name" value={e.bankAccountName} /> : null}
        <Row label={taxIdLabelForCountry(e.countryCode)} value={e.taxIdentifier ?? '—'} mono />
        <Row
          label={socialSecurityLabelForCountry(e.countryCode)}
          value={e.socialSecurityId ?? '—'}
          mono
        />
        {e.status === 'terminated' ? (
          <>
            <Row label="Terminated on" value={e.terminationDate ? formatDate(e.terminationDate) : '—'} />
            <Row label="Reason" value={e.terminationReason ?? '—'} />
          </>
        ) : null}
      </div>

      {e.nextOfKin ? <NextOfKinCard kin={e.nextOfKin} /> : null}
    </article>
  );
}

function formatGender(g: NonNullable<EmployeeResponse['gender']>): string {
  switch (g) {
    case 'male':
      return 'Male';
    case 'female':
      return 'Female';
    case 'non_binary':
      return 'Non-binary';
    case 'other':
      return 'Other';
    case 'prefer_not_to_say':
      return 'Prefer not to say';
  }
}

function formatEmploymentType(
  t: NonNullable<EmployeeResponse['employmentType']>,
): string {
  switch (t) {
    case 'full_time':
      return 'Full-time';
    case 'part_time':
      return 'Part-time';
    case 'contract':
      return 'Contract';
    case 'intern':
      return 'Intern';
  }
}

// ---------------------------------------------------------------------------
// EmployeeEditForm — inline PATCH /v1/employees/:id
// ---------------------------------------------------------------------------
//
// Mirrors the new-employee form's field set, minus the immutables
// (employeeNumber, countryCode, hireDate). Read-only display of those
// at the top so operators see what they can't change. Add status
// toggle (active ↔ on_leave; termination is its own endpoint).
//
// PATCH semantics:
//   - undefined → leave column alone
//   - null      → clear column
//   - string    → set to value
// We diff form values against the original employee record and only
// send fields that changed. Avoids write amplification on the audit
// log + makes the diffs human-readable in the trail.

const EmployeeEditFormSchema = z
  .object({
    firstName: z.string().min(1).max(100),
    middleName: z.string().max(100).or(z.literal('')).optional(),
    lastName: z.string().min(1).max(100),
    email: z.string().email().max(255).or(z.literal('')).optional(),
    phone: z.string().min(4).max(30).or(z.literal('')).optional(),
    gender: z
      .enum(['male', 'female', 'non_binary', 'other', 'prefer_not_to_say'])
      .or(z.literal(''))
      .optional(),
    stateCode: z
      .string()
      .regex(/^[A-Z]{2}$/, 'Two uppercase letters')
      .or(z.literal(''))
      .optional(),
    taxIdentifier: z.string().max(50).or(z.literal('')).optional(),
    socialSecurityId: z.string().max(50).or(z.literal('')).optional(),
    dateOfBirth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
      .or(z.literal(''))
      .optional(),
    homeAddress: z.string().max(500).or(z.literal('')).optional(),
    nokName: z.string().max(100).or(z.literal('')).optional(),
    nokRelationship: z.string().max(50).or(z.literal('')).optional(),
    nokPhone: z.string().max(30).or(z.literal('')).optional(),
    nokEmail: z.string().email().max(255).or(z.literal('')).optional(),
    nokAddress: z.string().max(500).or(z.literal('')).optional(),
    department: z.string().max(100).or(z.literal('')).optional(),
    position: z.string().max(100).or(z.literal('')).optional(),
    employmentType: z
      .enum(['full_time', 'part_time', 'contract', 'intern'])
      .or(z.literal(''))
      .optional(),
    workLocation: z.string().max(100).or(z.literal('')).optional(),
    /// Manager FK (cuid) or empty = no manager (ADR 0008 D19). The
    /// server rejects same-org-violations and cycles at write time.
    managerId: z.string().max(100).or(z.literal('')).optional(),
    bankSwiftCode: z
      .string()
      .regex(
        /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
        '8 or 11 chars: 6 letters + 2 alphanum + optional 3 alphanum',
      )
      .or(z.literal(''))
      .optional(),
    bankName: z.string().max(100).or(z.literal('')).optional(),
    bankAccountNumber: z.string().max(34).or(z.literal('')).optional(),
    bankAccountName: z.string().max(150).or(z.literal('')).optional(),
    status: z.enum(['active', 'on_leave']),
    // ADR 0010 Phase 4 — country is editable ONLY for international_remote
    // (the form renders it read-only otherwise; the server also enforces this
    // via 400 country_locked). Included so the patch can carry it.
    countryCode: z.enum(['NG', 'GH', 'CD']).optional(),
  })
  // Mirrors employeeNew's NOK refine: if any NOK field is filled,
  // name + relationship + phone are required. Half-filled emergency
  // contacts are useless.
  .refine(
    (v) => {
      const any = v.nokName || v.nokRelationship || v.nokPhone || v.nokEmail || v.nokAddress;
      if (!any) return true;
      return Boolean(v.nokName && v.nokRelationship && v.nokPhone);
    },
    {
      message: 'If filling next of kin, name + relationship + phone are required',
      path: ['nokName'],
    },
  );
type EmployeeEditFormValues = z.infer<typeof EmployeeEditFormSchema>;

/**
 * Returns a refined version of EmployeeEditFormSchema that adds
 * country-specific bank account validation. Memoize by countryCode in
 * the component to keep the zodResolver stable across re-renders.
 */
function makeEmployeeEditSchema(countryCode: string) {
  return EmployeeEditFormSchema.superRefine((data, ctx) => {
    if (
      countryCode === 'NG' &&
      data.bankAccountNumber &&
      data.bankAccountNumber !== '' &&
      !/^\d{10}$/.test(data.bankAccountNumber)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nigerian bank account numbers must be exactly 10 digits (NUBAN)',
        path: ['bankAccountNumber'],
      });
    }
  });
}

function EmployeeEditForm({
  employee,
  onCancel,
  onSaved,
}: {
  employee: EmployeeResponse;
  onCancel: () => void;
  onSaved: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  // Manager-candidate list (ADR 0008 D19). Fetch active employees in
  // the org and filter out self client-side. Mokoafrika scale (~200
  // employees) makes server-side filtering unnecessary; the existing
  // /v1/employees pagination caps at 200 so one request covers the
  // realistic case. A larger-tenant future can add an
  // `?excludeForManagerOf=<id>` query param + server filter without
  // changing the form.
  const managerCandidatesQuery = useQuery({
    queryKey: ['employees', 'manager-candidates'],
    queryFn: () => listEmployees({ status: 'active', limit: 200 }),
  });
  const managerOptions: Array<{ value: string; label: string }> = [
    { value: '', label: '(No direct manager)' },
    ...((managerCandidatesQuery.data?.employees ?? [])
      // Exclude self — an employee can't manage themselves (the
      // server enforces this too; client filter is UX). Excluding
      // direct reports here would require an extra reports lookup;
      // server-side cycle detection covers the case.
      .filter((e) => e.id !== employee.id)
      .map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName} (${e.employeeNumber})`,
      }))),
  ];

  const schema = useMemo(
    () => makeEmployeeEditSchema(employee.countryCode),
    [employee.countryCode],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmployeeEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: employee.firstName,
      middleName: employee.middleName ?? '',
      lastName: employee.lastName,
      email: employee.email ?? '',
      phone: employee.phone ?? '',
      gender: employee.gender ?? '',
      stateCode: employee.stateCode ?? '',
      taxIdentifier: employee.taxIdentifier ?? '',
      socialSecurityId: employee.socialSecurityId ?? '',
      dateOfBirth: employee.dateOfBirth ?? '',
      homeAddress: employee.homeAddress ?? '',
      nokName: employee.nextOfKin?.name ?? '',
      nokRelationship: employee.nextOfKin?.relationship ?? '',
      nokPhone: employee.nextOfKin?.phoneNumber ?? '',
      nokEmail: employee.nextOfKin?.email ?? '',
      nokAddress: employee.nextOfKin?.address ?? '',
      department: employee.department ?? '',
      position: employee.position ?? '',
      employmentType: employee.employmentType ?? '',
      workLocation: employee.workLocation ?? '',
      managerId: employee.managerId ?? '',
      bankSwiftCode: employee.bankSwiftCode ?? '',
      bankName: employee.bankName ?? '',
      bankAccountNumber: employee.bankAccountNumber ?? '',
      bankAccountName: employee.bankAccountName ?? '',
      // status from the server includes 'terminated' but we never reach
      // edit-mode for terminated employees, so the union here is safe.
      status: employee.status === 'terminated' ? 'active' : employee.status,
      countryCode: employee.countryCode,
    },
  });

  // ADR 0010 Phase 4 — country is editable only for international_remote.
  const countryEditable = employee.payrollModel === 'international_remote';

  const mutation = useMutation({
    mutationFn: (input: UpdateEmployeeInput) => patchEmployee(employee.id, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'detail', employee.id],
      });
      await queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
      onSaved();
    },
  });

  // Build the PATCH body by diffing form values against the current
  // employee record. Every "" form value maps to null (clear), unless
  // the original was already null in which case we omit (no-op).
  function buildPatchBody(values: EmployeeEditFormValues): UpdateEmployeeInput {
    const patch: UpdateEmployeeInput = {};

    function diffString<K extends keyof UpdateEmployeeInput>(
      key: K,
      formValue: string | undefined,
      originalValue: string | null | undefined,
    ): void {
      const next: string | null =
        !formValue || formValue === '' ? null : formValue;
      const prev: string | null = originalValue ?? null;
      if (next !== prev) {
        // The Zod inferred shape lets us assign string-or-null to
        // these fields; the type cast keeps TS happy across the
        // generic key.
        (patch as Record<string, unknown>)[key as string] = next;
      }
    }

    if (values.firstName !== employee.firstName) patch.firstName = values.firstName;
    if (values.lastName !== employee.lastName) patch.lastName = values.lastName;
    diffString('middleName', values.middleName, employee.middleName);
    diffString('email', values.email, employee.email);
    diffString('phone', values.phone, employee.phone);
    diffString('gender', values.gender, employee.gender);
    diffString('stateCode', values.stateCode, employee.stateCode);
    diffString('taxIdentifier', values.taxIdentifier, employee.taxIdentifier);
    diffString('socialSecurityId', values.socialSecurityId, employee.socialSecurityId);
    diffString('dateOfBirth', values.dateOfBirth, employee.dateOfBirth);
    diffString('homeAddress', values.homeAddress, employee.homeAddress);
    diffString('department', values.department, employee.department);
    diffString('position', values.position, employee.position);
    diffString('employmentType', values.employmentType, employee.employmentType);
    diffString('workLocation', values.workLocation, employee.workLocation);
    diffString('managerId', values.managerId, employee.managerId);
    diffString('bankSwiftCode', values.bankSwiftCode, employee.bankSwiftCode);
    diffString('bankName', values.bankName, employee.bankName);
    diffString('bankAccountNumber', values.bankAccountNumber, employee.bankAccountNumber);
    diffString('bankAccountName', values.bankAccountName, employee.bankAccountName);
    if (values.status !== employee.status) patch.status = values.status;
    // ADR 0010 Phase 4 — only carry country when it's editable (international_remote)
    // and actually changed. The server is the authority (400 country_locked otherwise).
    if (
      countryEditable &&
      values.countryCode &&
      values.countryCode !== employee.countryCode
    ) {
      (patch as Record<string, unknown>).countryCode = values.countryCode;
    }

    // NextOfKin: build a candidate object from the form. Send null
    // if all NOK fields are empty (clear). Skip the field entirely
    // (undefined) if no NOK fields changed.
    const formNok = values.nokName || values.nokRelationship || values.nokPhone
      ? {
          name: values.nokName!,
          relationship: values.nokRelationship!,
          phoneNumber: values.nokPhone!,
          ...(values.nokEmail ? { email: values.nokEmail } : {}),
          ...(values.nokAddress ? { address: values.nokAddress } : {}),
        }
      : null;
    const prevNok = employee.nextOfKin ?? null;
    const nokChanged = JSON.stringify(formNok) !== JSON.stringify(prevNok);
    if (nokChanged) patch.nextOfKin = formNok;

    return patch;
  }

  async function onSubmit(values: EmployeeEditFormValues): Promise<void> {
    setServerError(null);
    try {
      const body = buildPatchBody(values);
      if (Object.keys(body).length === 0) {
        // Nothing changed — close the form silently. Server would
        // 400 on an empty PATCH body anyway (UpdateEmployeeInput
        // refines `Object.keys.length > 0`).
        onSaved();
        return;
      }
      await mutation.mutateAsync(body);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError(
          'Employee is terminated — terminated records are immutable. Create a new employee for rehire.',
        );
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Try again.');
      }
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 rounded border border-gray-800 bg-gray-950 p-6"
      noValidate
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl">Edit employee</h2>
          <p className="text-xs text-gray-500 mt-1">
            <span className="font-mono">{employee.employeeNumber}</span> ·{' '}
            {employee.countryCode}
            {employee.stateCode ? ` · ${employee.stateCode}` : ''} · hired{' '}
            {formatDate(employee.hireDate)}. Employee number and hire date are
            immutable{countryEditable ? '' : '; country is fixed for local employees'}.
          </p>
        </div>
      </header>

      <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <legend className="sr-only">Identity</legend>
        <Field
          id="edit-firstName"
          label="First name *"
          error={errors.firstName?.message}
          inputProps={register('firstName')}
        />
        <Field
          id="edit-middleName"
          label="Middle name"
          error={errors.middleName?.message}
          inputProps={register('middleName')}
        />
        <Field
          id="edit-lastName"
          label="Last name *"
          error={errors.lastName?.message}
          inputProps={register('lastName')}
        />
        <SelectField
          id="edit-gender"
          label="Gender"
          error={errors.gender?.message}
          inputProps={register('gender')}
          options={[
            { value: '', label: '— select —' },
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' },
            { value: 'non_binary', label: 'Non-binary' },
            { value: 'other', label: 'Other' },
            { value: 'prefer_not_to_say', label: 'Prefer not to say' },
          ]}
        />
        <Field
          id="edit-email"
          label="Email"
          type="email"
          error={errors.email?.message}
          inputProps={register('email')}
        />
        <Field
          id="edit-phone"
          label="Phone"
          error={errors.phone?.message}
          inputProps={register('phone')}
        />
        {/* ADR 0010 Phase 4 — country editable only for international_remote. */}
        {countryEditable ? (
          <div className="space-y-1">
            <SelectField
              id="edit-countryCode"
              label="Country (residence)"
              error={errors.countryCode?.message}
              inputProps={register('countryCode')}
              options={[
                { value: 'NG', label: 'NG' },
                { value: 'GH', label: 'GH' },
                { value: 'CD', label: 'CD' },
              ]}
            />
            <p className="text-xs text-gray-500">
              Country represents residence for international remote employees and
              can be updated as needed.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="block text-xs uppercase tracking-wide text-gray-400">Country</p>
            <input
              id="edit-countryCode"
              value={employee.countryCode}
              readOnly
              disabled
              aria-readonly="true"
              title="Country determines statutory payroll math for local employees and cannot be changed. Change the payroll model to International remote (owner/admin) to enable country edits."
              className="w-full rounded border border-gray-800 bg-gray-900/50 px-3 py-2 text-gray-500 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500">
              Country determines statutory payroll math for local employees and
              cannot be changed. To enable, set the payroll model to
              International remote (owner/admin).
            </p>
          </div>
        )}
        {employee.countryCode === 'NG' ? (
          <Field
            id="edit-stateCode"
            label="State code (e.g. LA)"
            error={errors.stateCode?.message}
            inputProps={register('stateCode')}
          />
        ) : null}
        <SelectField
          id="edit-status"
          label="Status *"
          error={errors.status?.message}
          inputProps={register('status')}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'on_leave', label: 'On leave' },
          ]}
        />
        <Field
          id="edit-taxIdentifier"
          label={taxIdLabelForCountry(employee.countryCode)}
          error={errors.taxIdentifier?.message}
          inputProps={register('taxIdentifier')}
        />
        <Field
          id="edit-socialSecurityId"
          label={socialSecurityLabelForCountry(employee.countryCode)}
          error={errors.socialSecurityId?.message}
          inputProps={register('socialSecurityId')}
        />
      </fieldset>

      <fieldset className="space-y-3 rounded border border-gray-800 bg-gray-900/40 p-4">
        <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
          Personal details
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            id="edit-dateOfBirth"
            label="Date of birth"
            type="date"
            error={errors.dateOfBirth?.message}
            inputProps={register('dateOfBirth')}
          />
          <Field
            id="edit-homeAddress"
            label="Home address"
            error={errors.homeAddress?.message}
            inputProps={register('homeAddress')}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded border border-gray-800 bg-gray-900/40 p-4">
        <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
          Employment details
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            id="edit-department"
            label="Department"
            placeholder="Engineering"
            error={errors.department?.message}
            inputProps={register('department')}
          />
          <Field
            id="edit-position"
            label="Position / Job title"
            placeholder="Senior Engineer"
            error={errors.position?.message}
            inputProps={register('position')}
          />
          <SelectField
            id="edit-employmentType"
            label="Work arrangement"
            error={errors.employmentType?.message}
            inputProps={register('employmentType')}
            options={[
              { value: '', label: '— select —' },
              { value: 'full_time', label: 'Full-time' },
              { value: 'part_time', label: 'Part-time' },
              { value: 'contract', label: 'Contract' },
              { value: 'intern', label: 'Intern' },
            ]}
          />
          <Field
            id="edit-workLocation"
            label="Work location"
            placeholder="Lagos HQ / Remote"
            error={errors.workLocation?.message}
            inputProps={register('workLocation')}
          />
          <SelectField
            id="edit-managerId"
            label="Manager"
            error={errors.managerId?.message}
            inputProps={register('managerId')}
            options={managerOptions}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded border border-gray-800 bg-gray-900/40 p-4">
        <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
          Banking
        </legend>
        <p className="text-xs text-gray-500">
          SWIFT/BIC is needed for international wire transfers. Bank name,
          account number, and account name appear on payslips and are used
          for domestic salary payments.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            id="edit-bankSwiftCode"
            label="SWIFT / BIC code"
            placeholder="GTBINGLA"
            error={errors.bankSwiftCode?.message}
            inputProps={register('bankSwiftCode')}
          />
          <Field
            id="edit-bankName"
            label="Bank name"
            placeholder="GTBank"
            error={errors.bankName?.message}
            inputProps={register('bankName')}
          />
          <Field
            id="edit-bankAccountNumber"
            label="Account number"
            placeholder="0123456789"
            error={errors.bankAccountNumber?.message}
            inputProps={register('bankAccountNumber')}
          />
          <Field
            id="edit-bankAccountName"
            label="Account name"
            placeholder="As it appears on the bank account"
            error={errors.bankAccountName?.message}
            inputProps={register('bankAccountName')}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded border border-gray-800 bg-gray-900/40 p-4">
        <legend className="px-2 text-xs uppercase tracking-wide text-gray-400">
          Next of kin (emergency contact)
        </legend>
        <p className="text-xs text-gray-500">
          Optional. If filling, name + relationship + phone are the
          minimum so it's actually usable in an emergency. Clear all
          fields to remove the contact.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            id="edit-nokName"
            label="Full name"
            error={errors.nokName?.message}
            inputProps={register('nokName')}
          />
          <Field
            id="edit-nokRelationship"
            label="Relationship"
            placeholder="Spouse / Parent / Sibling"
            error={errors.nokRelationship?.message}
            inputProps={register('nokRelationship')}
          />
          <Field
            id="edit-nokPhone"
            label="Phone"
            error={errors.nokPhone?.message}
            inputProps={register('nokPhone')}
          />
          <Field
            id="edit-nokEmail"
            label="Email (optional)"
            type="email"
            error={errors.nokEmail?.message}
            inputProps={register('nokEmail')}
          />
          <div className="sm:col-span-2">
            <Field
              id="edit-nokAddress"
              label="Address (optional)"
              error={errors.nokAddress?.message}
              inputProps={register('nokAddress')}
            />
          </div>
        </div>
      </fieldset>

      {serverError ? (
        <Alert variant="error">{serverError}</Alert>
      ) : null}

      <div className="flex gap-3 pt-1">
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {isSubmitting || mutation.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// Reusable input + select primitives for the edit form. Mirror the
// CompField/CompSelectField shape used elsewhere on this page —
// loosened `inputProps` typing so this form's register output works.
interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  error?: string;
  inputProps: UseFormRegisterReturn<string>;
}

function Field({ id, label, type = 'text', placeholder, error, inputProps }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

interface SelectFieldProps extends Omit<FieldProps, 'type' | 'placeholder'> {
  options: Array<{ value: string; label: string }>;
}

function SelectField({ id, label, error, inputProps, options }: SelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

function NextOfKinCard({ kin }: { kin: NextOfKin }): JSX.Element {
  return (
    <div className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        Next of kin
      </header>
      <Row label="Name" value={kin.name} />
      <Row label="Relationship" value={kin.relationship} />
      <Row label="Phone" value={kin.phoneNumber} mono />
      {kin.email ? <Row label="Email" value={kin.email} /> : null}
      {kin.address ? <Row label="Address" value={kin.address} /> : null}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between px-4 py-3 text-sm">
      <span className="text-gray-500 uppercase tracking-wide text-xs">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}

function StatusPill({ status }: { status: EmployeeResponse['status'] }): JSX.Element {
  const map: Record<EmployeeResponse['status'], string> = {
    active: 'bg-green-950 text-green-400 border-green-900',
    on_leave: 'bg-yellow-950 text-yellow-400 border-yellow-900',
    terminated: 'bg-gray-900 text-gray-500 border-gray-800',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${map[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ProvisionLoginModal — HR creates an employee login account
// ---------------------------------------------------------------------------
//
// Two phases:
//   1. Form: HR enters email + initial password; submit POSTs to
//      /v1/employees/:id/provision-user.
//   2. Success: show the email + password back to HR with a "copy"
//      affordance so they can hand the credentials to the employee
//      (email integration lands in a future PR).
//
// We deliberately don't dismiss the modal automatically on success —
// HR needs to actually copy the credentials before closing. Closing
// after a successful provision invalidates the queries so the
// employee detail page can show "has login" status in a future PR.
const ProvisionLoginFormSchema = z.object({
  email: z.string().email('Enter a valid email').max(255),
  role: z.enum(['hr', 'finance', 'admin', 'employee', 'it']),
});
type ProvisionLoginFormValues = z.infer<typeof ProvisionLoginFormSchema>;

function ProvisionLoginModal({
  employee,
  onClose,
}: {
  employee: EmployeeResponse;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    email: string;
    supersededPriorInvitation: boolean;
  } | null>(null);

  // Re-invite path: a login already exists but acceptance is still pending.
  // The modal reuses the same endpoint as a resend (corrects email, supersedes
  // the old link, mints a fresh one). Default the role to the existing one so
  // a resend doesn't silently downgrade it; 'owner' can't be provisioned here.
  const pendingUser = employee.user?.status === 'invited' ? employee.user : null;
  const isResend = pendingUser !== null;
  const defaultRole =
    pendingUser && pendingUser.role !== 'owner' ? pendingUser.role : 'employee';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProvisionLoginFormValues>({
    resolver: zodResolver(ProvisionLoginFormSchema),
    defaultValues: {
      email: employee.email ?? '',
      role: defaultRole,
    },
  });

  const mutation = useMutation({
    mutationFn: (input: ProvisionLoginFormValues) =>
      provisionEmployeeUser(employee.id, input),
    onSuccess: async (result) => {
      setSuccess({
        email: result.email,
        supersededPriorInvitation: result.supersededPriorInvitation,
      });
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'detail', employee.id],
      });
    },
  });

  async function onSubmit(values: ProvisionLoginFormValues): Promise<void> {
    setServerError(null);
    try {
      await mutation.mutateAsync(values);
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Could not provision the login. Try again.');
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Provision employee login"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="font-serif text-xl">
            {isResend ? 'Resend invitation' : 'Provision login'}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {isResend
              ? `Send ${employee.firstName} a fresh invitation link. The previous link stops working — correct the login email below if the first one bounced.`
              : `Create an account for ${employee.firstName} and email them a one-time link to set their own password.`}
          </p>
        </header>

        {success ? (
          <div className="space-y-4">
            <div className="rounded border border-green-900 bg-green-950/40 p-4 text-sm">
              <p className="text-green-400 font-medium mb-2">
                Invitation email sent to {success.email}.
              </p>
              <p className="text-xs text-gray-400">
                They&apos;ll set their password via the link. It expires in 7
                days and can be used once.
              </p>
            </div>
            {success.supersededPriorInvitation ? (
              <p className="rounded border border-amber/40 bg-amber/10 p-3 text-xs text-amber">
                A previous pending invitation to this email was automatically
                superseded. Only the new link is valid.
              </p>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-1">
              <label
                htmlFor="provision-email"
                className="block text-xs uppercase tracking-wide text-gray-400"
              >
                Login email
              </label>
              <input
                id="provision-email"
                type="email"
                aria-invalid={errors.email ? 'true' : 'false'}
                className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white"
                {...register('email')}
              />
              {errors.email ? (
                <p className="text-xs text-red-400">{errors.email.message}</p>
              ) : null}
              <p className="text-[11px] text-gray-600">
                Doesn&apos;t have to be the same as the employee&apos;s
                contact email — this is just for login.
              </p>
            </div>

            <div className="space-y-1">
              <label
                htmlFor="provision-role"
                className="block text-xs uppercase tracking-wide text-gray-400"
              >
                Role
              </label>
              <select
                id="provision-role"
                aria-invalid={errors.role ? 'true' : 'false'}
                className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
                {...register('role')}
              >
                {PROVISIONABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {errors.role ? (
                <p className="text-xs text-red-400">{errors.role.message}</p>
              ) : null}
            </div>

            {serverError ? (
              <Alert variant="error">{serverError}</Alert>
            ) : null}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || mutation.isPending}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {isSubmitting || mutation.isPending
                  ? 'Sending invitation...'
                  : isResend
                    ? 'Resend invitation'
                    : 'Send invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const RoleChangeFormSchema = z.object({
  role: z.enum(['hr', 'finance', 'admin', 'employee', 'it']),
  reason: z.string().max(500).optional(),
});
type RoleChangeFormValues = z.infer<typeof RoleChangeFormSchema>;

function RoleChangeModal({
  employee,
  currentRole,
  onClose,
}: {
  employee: EmployeeResponse;
  currentRole: string;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ email: string; role: string } | null>(null);

  // Offer every provisionable role except the one the user already has.
  const options = PROVISIONABLE_ROLES.filter((r) => r.value !== currentRole);
  const defaultRole = options[0]?.value ?? 'employee';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RoleChangeFormValues>({
    resolver: zodResolver(RoleChangeFormSchema),
    defaultValues: { role: defaultRole, reason: '' },
  });

  const mutation = useMutation({
    mutationFn: (input: RoleChangeFormValues) =>
      changeUserRole(employee.id, {
        role: input.role,
        reason: input.reason?.trim() ? input.reason.trim() : undefined,
      }),
    onSuccess: async (result) => {
      setSuccess({ email: result.email, role: ROLE_LABELS[result.role] ?? result.role });
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'detail', employee.id],
      });
    },
  });

  async function onSubmit(values: RoleChangeFormValues): Promise<void> {
    setServerError(null);
    try {
      await mutation.mutateAsync(values);
    } catch (err) {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Could not change the role. Try again.');
      }
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change user role"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="font-serif text-xl">Change role</h2>
          <p className="text-xs text-gray-500 mt-1">
            Change {employee.firstName}&apos;s access level. A notification
            email is sent to the affected user.
          </p>
        </header>

        {success ? (
          <div className="space-y-4">
            <div className="rounded border border-green-900 bg-green-950/40 p-4 text-sm">
              <p className="text-green-400 font-medium">
                Role changed to {success.role}.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Notification email sent to {success.email}.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div className="space-y-1">
              <p className="block text-xs uppercase tracking-wide text-gray-400">
                Current role
              </p>
              <p className="text-sm text-white">
                {ROLE_LABELS[currentRole] ?? currentRole}
              </p>
            </div>

            <div className="space-y-1">
              <label
                htmlFor="role-change-role"
                className="block text-xs uppercase tracking-wide text-gray-400"
              >
                New role
              </label>
              <select
                id="role-change-role"
                aria-invalid={errors.role ? 'true' : 'false'}
                className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
                {...register('role')}
              >
                {options.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {errors.role ? (
                <p className="text-xs text-red-400">{errors.role.message}</p>
              ) : null}
            </div>

            <div className="space-y-1">
              <label
                htmlFor="role-change-reason"
                className="block text-xs uppercase tracking-wide text-gray-400"
              >
                Reason (optional)
              </label>
              <textarea
                id="role-change-reason"
                rows={2}
                className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
                {...register('reason')}
              />
            </div>

            {serverError ? (
              <Alert variant="error">{serverError}</Alert>
            ) : null}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || mutation.isPending}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {isSubmitting || mutation.isPending ? 'Changing...' : 'Change role'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ADR 0010 Phase 4 — change-payroll-model modal (mirrors RoleChangeModal)
// ---------------------------------------------------------------------------

const PAYROLL_MODEL_LABELS: Record<string, string> = {
  local_employee: 'Local employee',
  international_remote: 'International remote',
};

function PayrollModelChangeModal({
  employee,
  onClose,
}: {
  employee: EmployeeResponse;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const current = employee.payrollModel;
  // Binary toggle: the only target is the other model.
  const target: 'local_employee' | 'international_remote' =
    current === 'local_employee' ? 'international_remote' : 'local_employee';

  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  // Stores the model the change actually RESULTED in (from the API response),
  // not `target` — `target` is derived from the live `employee` prop, which
  // flips after the post-success query invalidation/refetch.
  const [success, setSuccess] = useState<'local_employee' | 'international_remote' | null>(null);
  // null = form mode; set = misclassification warning (soft, with confirm). Stores
  // the structured context so the copy is built here, not from the backend's
  // developer-facing `message` field.
  const [warning, setWarning] = useState<{ employeeCountry: string; primaryCountry: string } | null>(null);
  const [pending, setPending] = useState(false);

  const reasonValid = reason.trim().length > 0;

  async function submit(confirm: boolean): Promise<void> {
    setServerError(null);
    setPending(true);
    try {
      const result = await changePayrollModel(employee.id, {
        payrollModel: target,
        reason: reason.trim(),
        confirmMisclassificationWarning: confirm,
      });
      await queryClient.invalidateQueries({
        queryKey: ['employees', 'detail', employee.id],
      });
      setWarning(null);
      // Use the response's value — robust against the employee prop flipping
      // once the detail query refetches after invalidation.
      setSuccess(result.payrollModel);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        (err.body as { warningCode?: string } | undefined)?.warningCode ===
          'payroll_model_misclassification'
      ) {
        const body = err.body as {
          context?: { employeeCountry?: string; primaryCountry?: string };
        } | undefined;
        setWarning({
          employeeCountry: body?.context?.employeeCountry ?? employee.countryCode,
          primaryCountry: body?.context?.primaryCountry ?? employee.countryCode,
        });
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Could not change the payroll model. Try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change payroll model"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="font-serif text-xl">Change payroll model</h2>
        </header>

        {success ? (
          <div className="space-y-4">
            <div className="rounded border border-green-900 bg-green-950/40 p-4 text-sm">
              <p className="text-green-400 font-medium">
                Payroll model changed to {PAYROLL_MODEL_LABELS[success]}.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium"
              >
                Done
              </button>
            </div>
          </div>
        ) : warning ? (
          <div className="space-y-4">
            <div className="rounded border border-amber/40 bg-amber/10 p-4 text-sm space-y-2">
              <p className="text-amber font-medium">⚠ Likely misclassification</p>
              <p className="text-gray-300">
                {employee.firstName} {employee.lastName} currently resides in{' '}
                {warning.primaryCountry}, the same as your organization&rsquo;s
                primary country. International remote payroll model is typically
                used for employees who live in a different country. Confirm to
                proceed only if this assignment is intentional.
              </p>
            </div>
            <div className="space-y-1">
              <p className="block text-xs uppercase tracking-wide text-gray-400">Reason</p>
              <p className="text-sm text-gray-300">{reason.trim()}</p>
            </div>
            {serverError ? (
              <Alert variant="error">{serverError}</Alert>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => void submit(true)}
                className="rounded bg-amber text-black px-4 py-2 text-sm font-medium hover:bg-amber/90 disabled:opacity-50"
              >
                {pending ? 'Changing…' : 'Proceed anyway'}
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(false);
            }}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-1">
              <p className="block text-xs uppercase tracking-wide text-gray-400">Currently</p>
              <p className="text-sm text-white">{PAYROLL_MODEL_LABELS[current] ?? current}</p>
            </div>
            <div className="space-y-1">
              <p className="block text-xs uppercase tracking-wide text-gray-400">New payroll model</p>
              <p className="text-sm text-white">{PAYROLL_MODEL_LABELS[target]}</p>
            </div>
            <div className="space-y-1">
              <label
                htmlFor="payroll-model-reason"
                className="block text-xs uppercase tracking-wide text-gray-400"
              >
                Reason (required)
              </label>
              <textarea
                id="payroll-model-reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
              />
              <p className="text-xs text-gray-500">
                Briefly explain why — this is logged for audit purposes.
              </p>
            </div>
            {serverError ? (
              <Alert variant="error">{serverError}</Alert>
            ) : null}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-700 px-3 py-2 text-sm hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!reasonValid || pending}
                className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {pending ? 'Changing…' : 'Change payroll model'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compensation section
// ---------------------------------------------------------------------------

const CompensationForm = z.object({
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  grossSalary: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'Amount with up to 4 decimals (e.g. 500000 or 500000.0000)')
    .refine((v) => Number(v) > 0, 'Must be greater than zero'),
  currency: z.enum(['NGN', 'GHS', 'CDF', 'USD']),
  payFrequency: z.enum(['monthly', 'bi_weekly', 'weekly']),
});
type CompensationForm = z.infer<typeof CompensationForm>;

function CompensationSection({ employee }: { employee: EmployeeResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['compensations', employee.id],
    queryFn: () => listCompensations(employee.id),
  });

  // Currency options follow the server's country-vs-currency rule:
  // local currency OR USD for expats. Filtering at the dropdown level
  // means operators never see an invalid combo — the server enforces
  // the same check and returns 400 if anything slips through.
  const currencyOptions = allowedCurrenciesForCountry(employee.countryCode).map(
    (code) => ({
      value: code,
      label: code === 'USD' ? 'USD (expat)' : code,
    }),
  );
  const defaultCurrency = currencyOptions[0]!.value as CompensationForm['currency'];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CompensationForm>({
    resolver: zodResolver(CompensationForm),
    defaultValues: {
      effectiveFrom: new Date().toISOString().slice(0, 10),
      grossSalary: '',
      currency: defaultCurrency,
      payFrequency: 'monthly',
    },
  });

  const mutation = useMutation({
    mutationFn: createCompensation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['compensations', employee.id] });
      reset({
        effectiveFrom: new Date().toISOString().slice(0, 10),
        grossSalary: '',
        currency: defaultCurrency,
        payFrequency: 'monthly',
      });
      setFormOpen(false);
      setServerError(null);
    },
  });

  async function onSubmit(values: CompensationForm): Promise<void> {
    setServerError(null);
    try {
      // Normalize grossSalary to 4 decimals so the server's MoneyAmount
      // regex always passes.
      const normalized = Number(values.grossSalary).toFixed(4);
      await mutation.mutateAsync({
        employeeId: employee.id,
        effectiveFrom: values.effectiveFrom,
        grossSalary: normalized,
        currency: values.currency,
        payFrequency: values.payFrequency,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError('Employee is terminated — compensation cannot be set.');
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Try again.');
      }
    }
  }

  const comps = data?.compensations ?? [];
  const current = comps.find((c) => c.effectiveTo === null) ?? null;
  const history = comps.filter((c) => c.id !== current?.id);
  const canEdit = employee.status !== 'terminated';

  return (
    <article className="space-y-3 pt-4">
      <header className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Compensation</h2>
        {canEdit && !formOpen ? (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="rounded bg-white text-black text-xs font-medium px-3 py-1 hover:bg-gray-200"
          >
            {current ? 'Set new compensation' : 'Set compensation'}
          </button>
        ) : null}
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading compensation...</p>
      ) : null}

      {!isLoading && !current && !formOpen ? (
        <div className="rounded border border-dashed border-yellow-900 bg-yellow-950/20 p-4 text-sm">
          <p className="text-yellow-400">No compensation set.</p>
          <p className="text-gray-400 mt-1">
            Payroll runs will fail for this employee until a compensation is set.
          </p>
        </div>
      ) : null}

      {current ? (
        <div className="rounded border border-gray-900 p-4 space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Current (as of {formatDate(current.effectiveFrom)})
          </p>
          <p className="text-xl font-medium">
            {formatMoney(current.grossSalary, current.currency)}
          </p>
          <p className="text-xs text-gray-500">
            {current.payFrequency.replace('_', '-')} · effective {formatDate(current.effectiveFrom)}
          </p>
        </div>
      ) : null}

      {formOpen ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="rounded border border-gray-800 bg-gray-950 p-4 space-y-3"
          noValidate
        >
          <h3 className="text-xs uppercase tracking-wide text-gray-400">
            {current ? 'Replace current compensation' : 'First compensation'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CompField
              id="grossSalary"
              label="Gross salary *"
              placeholder="500000"
              error={errors.grossSalary?.message}
              inputProps={register('grossSalary')}
            />
            <CompSelectField
              id="currency"
              label="Currency *"
              error={errors.currency?.message}
              inputProps={register('currency')}
              options={currencyOptions}
            />
            <CompField
              id="effectiveFrom"
              label="Effective from *"
              type="date"
              error={errors.effectiveFrom?.message}
              inputProps={register('effectiveFrom')}
            />
            <CompSelectField
              id="payFrequency"
              label="Frequency *"
              error={errors.payFrequency?.message}
              inputProps={register('payFrequency')}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'bi_weekly', label: 'Bi-weekly' },
                { value: 'weekly', label: 'Weekly' },
              ]}
            />
          </div>

          {current ? (
            <p className="text-xs text-gray-500">
              Submitting closes the current compensation on the effective-from date
              and creates a new one. History is preserved.
            </p>
          ) : null}

          {serverError ? (
            <Alert variant="error" compact>{serverError}</Alert>
          ) : null}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              className="rounded bg-white text-black text-xs font-medium px-3 py-1 disabled:opacity-50"
            >
              {isSubmitting || mutation.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setServerError(null);
              }}
              className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {history.length > 0 ? (
        <details className="rounded border border-gray-900 p-3">
          <summary className="text-xs uppercase tracking-wide text-gray-500 cursor-pointer">
            History ({history.length})
          </summary>
          <ul className="mt-2 space-y-1 text-sm">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between">
                <span className="text-gray-400">
                  {formatDate(h.effectiveFrom)} →{' '}
                  {h.effectiveTo ? formatDate(h.effectiveTo) : 'present'}
                </span>
                <span className="font-mono text-xs">
                  {formatMoney(h.grossSalary, h.currency as CompensationResponse['currency'])}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </article>
  );
}

interface CompFieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  error?: string;
  // `UseFormRegisterReturn<string>` accepts the register output from any
  // form on this page — both the compensation and pay-component forms
  // share these primitives now. Without the `<string>` widening,
  // TypeScript pins the field-name union to whichever form was annotated
  // first.
  inputProps: UseFormRegisterReturn<string>;
}

function CompField({ id, label, type = 'text', placeholder, error, inputProps }: CompFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

interface CompSelectFieldProps extends Omit<CompFieldProps, 'type' | 'placeholder'> {
  options: Array<{ value: string; label: string }>;
}

function CompSelectField({ id, label, error, inputProps, options }: CompSelectFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
        {...inputProps}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pay components section — recurring + one-time payments
// ---------------------------------------------------------------------------
//
// Allowances (recurring) plus one-time payments — bonus, 13th-month,
// severance, final settlement. All flow through the calculator as
// taxable gross additions in Wave 1 jurisdictions.
//
// Scope (matches the API):
//   - allowance / bonus / thirteenth_month / severance / final_settlement.
//   - kind=deduction is rejected: it would reduce net without reducing
//     taxable gross — separate code path the calculator doesn't have.
//   - Append-only: edit by ending + creating a new row, like Compensation.

const KIND_OPTIONS = [
  { value: 'allowance', label: 'Allowance — recurring (housing, transport, other)' },
  { value: 'bonus', label: 'Bonus — one-time taxable addition' },
  { value: 'thirteenth_month', label: '13th-month — annual statutory / customary' },
  { value: 'severance', label: 'Severance — termination payment' },
  { value: 'final_settlement', label: 'Final settlement — accrued + severance combined' },
] as const;

const NATURE_OPTIONS_ALLOWANCE = [
  { value: 'housing', label: 'Housing — taxable (rent-relief carve-outs in NG)' },
  { value: 'transport', label: 'Transport — taxable (partial-exempt in some countries)' },
  { value: 'other_taxable', label: 'Other (taxable) — adds to gross + tax basis' },
  { value: 'other_nontaxable', label: 'Other (non-taxable) — reimbursement, per-diem' },
] as const;

// Bonus / 13th-month / severance / final-settlement are always taxable
// gross additions in Wave 1 jurisdictions. Restrict the dropdown to
// taxable natures so operators can't misclassify and underpay tax.
const NATURE_OPTIONS_ONE_TIME = [
  { value: 'other_taxable', label: 'Taxable gross addition (default for one-time payments)' },
] as const;

const PayComponentForm = z
  .object({
    kind: z.enum([
      'allowance',
      'bonus',
      'thirteenth_month',
      'severance',
      'final_settlement',
    ]),
    nature: z.enum(['housing', 'transport', 'other_taxable', 'other_nontaxable']),
    label: z.string().min(1, 'Label is required').max(100),
    amount: z
      .string()
      .regex(/^\d+(\.\d{1,4})?$/, 'Amount with up to 4 decimals')
      .refine((v) => Number(v) > 0, 'Must be greater than zero'),
    currency: z.enum(['NGN', 'GHS', 'CDF', 'USD']),
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    /**
     * For one-time payments (bonus / 13th / severance / final), the
     * server expects effectiveTo == effectiveFrom. For allowances,
     * leave blank — the component stays open until ended via PATCH.
     */
    effectiveTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
      .or(z.literal(''))
      .optional(),
    note: z.string().max(500).or(z.literal('')).optional(),
  })
  .refine(
    (v) => v.kind === 'allowance' || v.nature !== 'other_nontaxable',
    {
      message: 'One-time payments must be taxable. Pick a taxable nature.',
      path: ['nature'],
    },
  );
type PayComponentForm = z.infer<typeof PayComponentForm>;

function PayComponentSection({ employee }: { employee: EmployeeResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [endingId, setEndingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pay-components', employee.id],
    queryFn: () => listPayComponents(employee.id),
  });

  const currencyOptions = allowedCurrenciesForCountry(employee.countryCode).map(
    (code) => ({
      value: code,
      label: code === 'USD' ? 'USD (expat)' : code,
    }),
  );
  const defaultCurrency = currencyOptions[0]!.value as PayComponentForm['currency'];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PayComponentForm>({
    resolver: zodResolver(PayComponentForm),
    defaultValues: {
      kind: 'allowance',
      nature: 'housing',
      label: '',
      amount: '',
      currency: defaultCurrency,
      effectiveFrom: new Date().toISOString().slice(0, 10),
      effectiveTo: '',
      note: '',
    },
  });

  const watchedKind = watch('kind');
  const isOneTime = watchedKind !== 'allowance';
  // For one-time payments the dropdown only offers taxable natures
  // (bonus / 13th / severance / final-settlement are always taxable
  // gross additions in Wave 1). For allowances, the full set including
  // non-taxable. Schema refine on submit catches invalid combinations
  // if the operator switches kind without re-picking nature.
  const natureOptions = isOneTime
    ? NATURE_OPTIONS_ONE_TIME
    : NATURE_OPTIONS_ALLOWANCE;

  const createMutation = useMutation({
    mutationFn: createPayComponent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['pay-components', employee.id],
      });
      reset({
        kind: 'allowance',
        nature: 'housing',
        label: '',
        amount: '',
        currency: defaultCurrency,
        effectiveFrom: new Date().toISOString().slice(0, 10),
        effectiveTo: '',
        note: '',
      });
      setFormOpen(false);
      setServerError(null);
    },
  });

  const endMutation = useMutation({
    mutationFn: ({ id, effectiveTo, reason }: { id: string; effectiveTo: string; reason: string }) =>
      endPayComponent(id, { effectiveTo, reason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['pay-components', employee.id],
      });
      setEndingId(null);
    },
  });

  async function onSubmit(values: PayComponentForm): Promise<void> {
    setServerError(null);
    try {
      const normalized = Number(values.amount).toFixed(4);
      // One-time payments: if operator left effectiveTo blank, set it
      // to effectiveFrom so the row represents a single-period payment.
      // Recurring allowances stay open (no effectiveTo).
      const effectiveTo = values.effectiveTo
        ? values.effectiveTo
        : values.kind !== 'allowance'
          ? values.effectiveFrom
          : undefined;
      await createMutation.mutateAsync({
        employeeId: employee.id,
        kind: values.kind,
        nature: values.nature,
        label: values.label,
        amount: normalized,
        currency: values.currency,
        effectiveFrom: values.effectiveFrom,
        ...(effectiveTo ? { effectiveTo } : {}),
        ...(values.note ? { note: values.note } : {}),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError('Employee is terminated — pay components cannot be added.');
      } else if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Try again.');
      }
    }
  }

  async function handleEnd(component: PayComponentResponse): Promise<void> {
    // Browser prompt over a custom modal for v1 — keeps the surface area
    // small. ReasonModal can replace this if it gets used elsewhere.
    const reason = window.prompt(
      `End "${component.label}"? Provide a reason (≥3 chars) for the audit trail.`,
    );
    if (!reason || reason.trim().length < 3) return;
    const today = new Date().toISOString().slice(0, 10);
    await endMutation.mutateAsync({
      id: component.id,
      effectiveTo: today,
      reason: reason.trim(),
    });
  }

  const components = data?.components ?? [];
  const canEdit = employee.status !== 'terminated';

  return (
    <article className="space-y-3 pt-4">
      <header className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Pay components</h2>
        {canEdit && !formOpen ? (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="rounded bg-white text-black text-xs font-medium px-3 py-1 hover:bg-gray-200"
          >
            Add component
          </button>
        ) : null}
      </header>

      <p className="text-xs text-gray-500">
        Allowances (recurring), bonuses, 13th-month, severance, final
        settlement. Append-only — edit by ending a component and adding a
        new one with a later effective date.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading components...</p>
      ) : null}

      {!isLoading && components.length === 0 && !formOpen ? (
        <div className="rounded border border-dashed border-gray-800 p-4 text-sm text-gray-500">
          No pay components set. Basic salary is set under{' '}
          <span className="font-mono text-xs">Compensation</span>; add
          allowances, bonuses, severance, etc here.
        </div>
      ) : null}

      {components.length > 0 ? (
        <ul className="rounded border border-gray-900 divide-y divide-gray-900">
          {components.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2">
                  <NaturePill nature={c.nature} />
                  <span className="text-sm truncate">{c.label}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {formatDate(c.effectiveFrom)} →{' '}
                  {c.effectiveTo ? formatDate(c.effectiveTo) : 'present'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm">
                  {formatMoney(c.amount, c.currency)}
                </span>
                {canEdit && c.effectiveTo === null ? (
                  <button
                    type="button"
                    onClick={() => {
                      setEndingId(c.id);
                      void handleEnd(c).finally(() => setEndingId(null));
                    }}
                    disabled={endingId === c.id}
                    className="rounded border border-gray-700 text-xs px-2 py-1 hover:bg-gray-900 disabled:opacity-50"
                  >
                    {endingId === c.id ? 'Ending...' : 'End'}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {formOpen ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="rounded border border-gray-800 bg-gray-950 p-4 space-y-3"
          noValidate
        >
          <h3 className="text-xs uppercase tracking-wide text-gray-400">
            Add pay component
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CompSelectField
              id="kind"
              label="Kind *"
              error={errors.kind?.message}
              inputProps={register('kind')}
              options={KIND_OPTIONS as unknown as Array<{ value: string; label: string }>}
            />
            <CompSelectField
              id="nature"
              label="Tax treatment *"
              error={errors.nature?.message}
              inputProps={register('nature')}
              options={natureOptions as unknown as Array<{ value: string; label: string }>}
            />
            <CompField
              id="label"
              label="Label *"
              placeholder={
                isOneTime
                  ? 'e.g. Q1 performance bonus, Severance package'
                  : 'e.g. Housing allowance, Driver subsidy'
              }
              error={errors.label?.message}
              inputProps={register('label')}
            />
            <CompField
              id="amount"
              label="Amount *"
              placeholder="20000"
              error={errors.amount?.message}
              inputProps={register('amount')}
            />
            <CompSelectField
              id="currency"
              label="Currency *"
              error={errors.currency?.message}
              inputProps={register('currency')}
              options={currencyOptions}
            />
            <CompField
              id="effectiveFrom"
              label={isOneTime ? 'Pay date *' : 'Effective from *'}
              type="date"
              error={errors.effectiveFrom?.message}
              inputProps={register('effectiveFrom')}
            />
            <CompField
              id="note"
              label="Note (audit trail, optional)"
              placeholder={
                isOneTime
                  ? 'Approval ref, board minutes, etc.'
                  : 'HR letter HR-2026-014'
              }
              error={errors.note?.message}
              inputProps={register('note')}
            />
          </div>

          {isOneTime ? (
            <p className="text-xs text-gray-500">
              One-time payments are pinned to a single pay date. The
              component will appear in the next payroll run for that
              period and not recur. Always taxable.
            </p>
          ) : null}

          {serverError ? (
            <Alert variant="error" compact>{serverError}</Alert>
          ) : null}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="rounded bg-white text-black text-xs font-medium px-3 py-1 disabled:opacity-50"
            >
              {isSubmitting || createMutation.isPending ? 'Saving...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setServerError(null);
              }}
              className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </article>
  );
}

function NaturePill({ nature }: { nature: PayComponentResponse['nature'] }): JSX.Element {
  // Color-codes whether the component contributes to taxable gross.
  // other_nontaxable is the only "non-taxable" path; everything else
  // adds to gross. See isTaxableNature() in @kora/types.
  const color =
    nature === 'other_nontaxable'
      ? 'bg-gray-900 text-gray-400 border-gray-800'
      : nature === 'housing'
        ? 'bg-indigo/10 text-indigo border-indigo/40'
        : nature === 'transport'
          ? 'bg-blue-950 text-blue-300 border-blue-900'
          : nature === 'basic'
            ? 'bg-emerald/10 text-emerald border-emerald/40'
            : 'bg-gray-800 text-gray-300 border-gray-700';
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${color}`}>
      {nature.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Documents section — Tier 2 of Mokoafrika's HRIS-consolidation asks
// ---------------------------------------------------------------------------
//
// Operators upload certificates, contracts, JDs, ID scans against the
// employee record. Bytes live behind DocumentStorage on the server
// (LocalDiskStorage in dev, R2/S3 later); the FE only sees metadata
// in the list and triggers downloads via signed-URL-equivalent
// (auth'd fetch through the route).
//
// Mutation model: append-only. To "edit" a document, delete + re-upload.
// Delete requires a reason — the audit trail explains why a contract
// or ID disappeared.

// DOCUMENT_KIND_OPTIONS and formatBytes moved to ../lib/documents.ts and
// ../lib/format.ts so the new /me/documents page can share the same
// labels and size formatter. Single source of truth — if a kind label
// reads better as "Contract / NDA / addendum", both screens pick it up.

const DOCUMENT_ACCEPT = EMPLOYEE_DOCUMENT_ALLOWED_TYPES.join(',');

function DocumentsSection({ employee }: { employee: EmployeeResponse }): JSX.Element {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['employee-documents', employee.id],
    queryFn: () => listEmployeeDocuments(employee.id),
  });

  const uploadMutation = useMutation({
    mutationFn: uploadEmployeeDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employee-documents', employee.id],
      });
      setFormOpen(false);
      setServerError(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmployeeDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['employee-documents', employee.id],
      });
      setDeletingId(null);
    },
  });

  async function handleDelete(doc: EmployeeDocumentResponse): Promise<void> {
    const reason = window.prompt(
      `Delete "${doc.label}"? Provide a reason (≥3 chars) for the audit trail.`,
    );
    if (!reason || reason.trim().length < 3) return;
    setDeletingId(doc.id);
    try {
      await deleteMutation.mutateAsync({
        employeeId: employee.id,
        docId: doc.id,
        reason: reason.trim(),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        window.alert(`Delete failed: ${err.message}`);
      }
      setDeletingId(null);
    }
  }

  const documents = data?.documents ?? [];
  const canEdit = employee.status !== 'terminated';

  return (
    <article className="space-y-3 pt-4">
      <header className="flex items-center justify-between">
        <h2 className="font-serif text-xl">Documents</h2>
        {canEdit && !formOpen ? (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="rounded bg-white text-black text-xs font-medium px-3 py-1 hover:bg-gray-200"
          >
            Upload document
          </button>
        ) : null}
      </header>

      <p className="text-xs text-gray-500">
        Certificates, contracts, JDs, ID scans. Up to{' '}
        {formatBytes(EMPLOYEE_DOCUMENT_MAX_BYTES)} per file. PDF, JPG, PNG, DOC,
        DOCX. Append-only — delete + re-upload to replace; deletes are
        audit-logged with a required reason.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading documents...</p>
      ) : null}

      {!isLoading && documents.length === 0 && !formOpen ? (
        <div className="rounded border border-dashed border-gray-800 p-4 text-sm text-gray-500">
          No documents uploaded yet.
        </div>
      ) : null}

      {documents.length > 0 ? (
        <ul className="rounded border border-gray-900 divide-y divide-gray-900">
          {documents.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <DocumentKindPill kind={d.kind} />
                  <span className="text-sm truncate">{d.label}</span>
                </div>
                <span className="text-xs text-gray-500 truncate">
                  {d.filename} · {formatBytes(d.sizeBytes)} · uploaded{' '}
                  {formatDate(d.uploadedAt)}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={buildDocumentDownloadUrl(employee.id, d.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded border border-gray-700 text-xs px-2 py-1 hover:bg-gray-900"
                  // Note: this open-in-new-tab won't carry the Bearer
                  // token. For v1 with cookie-based session it works;
                  // for the JWT-only path, the FE could fetch + create
                  // an object URL instead. Future polish.
                >
                  Download
                </a>
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => void handleDelete(d)}
                    disabled={deletingId === d.id}
                    className="rounded border border-gray-700 text-xs px-2 py-1 hover:bg-gray-900 disabled:opacity-50"
                  >
                    {deletingId === d.id ? 'Deleting…' : 'Delete'}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {formOpen ? (
        <UploadDocumentForm
          employeeId={employee.id}
          onCancel={() => {
            setFormOpen(false);
            setServerError(null);
          }}
          onSubmit={async (args) => {
            setServerError(null);
            try {
              await uploadMutation.mutateAsync(args);
            } catch (err) {
              if (err instanceof ApiError && err.status === 413) {
                setServerError(
                  `File exceeds the ${formatBytes(EMPLOYEE_DOCUMENT_MAX_BYTES)} limit.`,
                );
              } else if (err instanceof ApiError && err.status === 415) {
                setServerError('That file type is not allowed.');
              } else if (err instanceof ApiError) {
                setServerError(err.message);
              } else {
                setServerError('Upload failed. Try again.');
              }
            }
          }}
          serverError={serverError}
          isSubmitting={uploadMutation.isPending}
        />
      ) : null}
    </article>
  );
}

function DocumentKindPill({
  kind,
}: {
  kind: EmployeeDocumentResponse['kind'];
}): JSX.Element {
  const color = (() => {
    switch (kind) {
      case 'contract':
        return 'bg-blue-950 text-blue-300 border-blue-900';
      case 'certificate':
        return 'bg-emerald/10 text-emerald border-emerald/40';
      case 'id_document':
        return 'bg-indigo/10 text-indigo border-indigo/40';
      case 'job_description':
        return 'bg-purple-950 text-purple-300 border-purple-900';
      case 'performance_review':
        return 'bg-yellow-950 text-yellow-400 border-yellow-900';
      default:
        return 'bg-gray-900 text-gray-400 border-gray-800';
    }
  })();
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${color} shrink-0`}>
      {kind.replace('_', ' ')}
    </span>
  );
}

function UploadDocumentForm({
  employeeId,
  onCancel,
  onSubmit,
  serverError,
  isSubmitting,
}: {
  employeeId: string;
  onCancel: () => void;
  onSubmit: (args: {
    employeeId: string;
    kind: EmployeeDocumentKind;
    label: string;
    file: File;
  }) => Promise<void>;
  serverError: string | null;
  isSubmitting: boolean;
}): JSX.Element {
  const [kind, setKind] = useState<EmployeeDocumentKind>('certificate');
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [clientError, setClientError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setClientError(null);
    if (!file) {
      setClientError('Pick a file to upload.');
      return;
    }
    if (file.size > EMPLOYEE_DOCUMENT_MAX_BYTES) {
      setClientError(
        `File is ${formatBytes(file.size)} — exceeds the ${formatBytes(EMPLOYEE_DOCUMENT_MAX_BYTES)} limit.`,
      );
      return;
    }
    if (!label.trim()) {
      setClientError('Label is required.');
      return;
    }
    await onSubmit({ employeeId, kind, label: label.trim(), file });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded border border-gray-800 bg-gray-950 p-4 space-y-3"
      noValidate
    >
      <h3 className="text-xs uppercase tracking-wide text-gray-400">
        Upload document
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label
            htmlFor="doc-kind"
            className="block text-xs uppercase tracking-wide text-gray-400"
          >
            Kind *
          </label>
          <select
            id="doc-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as EmployeeDocumentKind)}
            className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white focus:border-white focus:outline-none"
          >
            {DOCUMENT_KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label
            htmlFor="doc-label"
            className="block text-xs uppercase tracking-wide text-gray-400"
          >
            Label *
          </label>
          <input
            id="doc-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Employment contract 2026, BSc transcript"
            maxLength={120}
            className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
          />
        </div>
        <div className="sm:col-span-2 space-y-1">
          <label
            htmlFor="doc-file"
            className="block text-xs uppercase tracking-wide text-gray-400"
          >
            File * (max {formatBytes(EMPLOYEE_DOCUMENT_MAX_BYTES)})
          </label>
          <input
            id="doc-file"
            type="file"
            accept={DOCUMENT_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm text-gray-300 file:mr-3 file:rounded file:border-0 file:bg-gray-700 file:px-3 file:py-1 file:text-white hover:file:bg-gray-600"
          />
          {file ? (
            <p className="text-xs text-gray-500">
              {file.name} · {formatBytes(file.size)} · {file.type || 'unknown type'}
            </p>
          ) : null}
        </div>
      </div>

      {clientError ? (
        <Alert variant="error" compact>{clientError}</Alert>
      ) : null}
      {serverError ? (
        <Alert variant="error" compact>{serverError}</Alert>
      ) : null}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded bg-white text-black text-xs font-medium px-3 py-1 disabled:opacity-50"
        >
          {isSubmitting ? 'Uploading…' : 'Upload'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function LoadingState(): JSX.Element {
  return (
    <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
      Loading...
    </div>
  );
}

function ErrorState({ error }: { error: unknown }): JSX.Element {
  const is404 = error instanceof ApiError && error.status === 404;
  return (
    <Alert
      variant="error"
      title={is404 ? 'Employee not found' : 'Could not load employee'}
      action={
        <Link
          to="/employees"
          className="inline-block rounded border border-red-800 px-3 py-1 text-xs hover:bg-red-900/40"
        >
          Back to employees
        </Link>
      }
    >
      {is404
        ? 'This employee may have been deleted, or you may not have permission to view it.'
        : error instanceof Error
          ? error.message
          : 'Unknown error'}
    </Alert>
  );
}

/**
 * AssetsHeldSection (ADR-0024 Phase 4 — the terminate seam, FE-only). Shows the
 * assets currently in this employee's custody + history, via the existing
 * GET /v1/employees/:id/assets. This is where offboarding sees what a leaver
 * still holds — the surface exists precisely so an asset can honestly read as
 * "issued to a terminated employee" until physically returned. It NEVER blocks
 * or gates any action; termination proceeds regardless (per the 3a amendment).
 *
 * NOTE: the FE currently has no termination screen at all (terminateEmployee is
 * defined in the API client but wired nowhere), so this panel lives on the
 * employee detail page rather than a terminate modal.
 */
export function AssetsHeldSection({ employeeId }: { employeeId: string }): JSX.Element {
  const query = useQuery({ queryKey: ['employee', 'assets', employeeId], queryFn: () => listEmployeeAssets(employeeId) });
  const assets = query.data?.assets ?? [];
  const outstanding = assets.filter((a) => a.current);
  return (
    <div className="rounded border border-gray-900 p-5 space-y-3">
      <h2 className="font-serif text-xl">Assets held</h2>
      {query.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      {!query.isLoading && outstanding.length === 0 ? (
        <p className="text-sm text-gray-400">No company assets currently in this employee’s custody.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {outstanding.map((a) => (
            <li key={a.id} className="flex items-center justify-between">
              <span><span className="font-mono text-gray-300">{a.assetTag ?? '—'}</span> <span className="text-gray-400">· {a.name ?? '—'}</span></span>
              <span className="text-xs text-gray-500">since {fmtAssignedAt(a.assignedAt)}</span>
            </li>
          ))}
        </ul>
      )}
      {outstanding.length > 0 ? (
        <p className="text-xs text-gray-500">Outstanding assets stay issued until physically returned — offboarding does not auto-return them.</p>
      ) : null}
    </div>
  );
}

const TERMINATION_REASON_LABELS: Record<TerminationReason, string> = {
  resignation: 'Resignation',
  dismissal: 'Dismissal',
  retirement: 'Retirement',
  end_of_contract: 'End of contract',
  death: 'Death',
};

/**
 * TerminationSection — the off-boarding surface (closes the #271 gap; wires the
 * existing POST /v1/employees/:id/terminate, which was defined in the client but
 * mounted nowhere). For an active employee it offers the terminate action
 * (owner/admin/hr only — the endpoint is HR-gated); for a terminated one it
 * shows a read-only summary. It never gates anything: outstanding assets are a
 * reminder, not a block (per the ADR-0024 3a amendment).
 */
export function TerminationSection({ employee }: { employee: EmployeeResponse }): JSX.Element | null {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const canTerminate = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';

  if (employee.status === 'terminated') {
    return (
      <div className="rounded border border-gray-900 p-5 space-y-2">
        <h2 className="font-serif text-xl">Termination</h2>
        <p className="text-sm text-gray-300">
          Terminated{employee.terminationDate ? ` on ${formatDate(employee.terminationDate)}` : ''}
          {employee.terminationReason ? ` — ${TERMINATION_REASON_LABELS[employee.terminationReason as TerminationReason] ?? employee.terminationReason}` : ''}.
        </p>
        <p className="text-xs text-gray-500">Terminated records are immutable. To rehire, create a new employee.</p>
      </div>
    );
  }

  if (!canTerminate) return null;

  return (
    <div className="rounded border border-red-950 p-5 space-y-3">
      <h2 className="font-serif text-xl">Off-boarding</h2>
      <p className="text-sm text-gray-400">
        Terminating records the leaver, computes their final settlement, and cancels future-dated leave.
        It <span className="text-gray-300">cannot be undone</span> — a terminated record is immutable.
      </p>
      <button type="button" onClick={() => setOpen(true)} className="rounded border border-red-800 text-red-300 px-4 py-2 text-sm hover:bg-red-950/40">
        Terminate employment…
      </button>
      {open ? (
        <TerminateModal
          employee={employee}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['employees', 'detail', employee.id] });
            void queryClient.invalidateQueries({ queryKey: ['employees', 'list'] });
          }}
        />
      ) : null}
    </div>
  );
}

function TerminateModal({ employee, onClose, onDone }: { employee: EmployeeResponse; onClose: () => void; onDone: () => void }): JSX.Element {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<TerminationReason | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TerminateEmployeeResult | null>(null);

  // Non-blocking reminder — what does the leaver still hold? Never gates.
  const assetsQuery = useQuery({ queryKey: ['employee', 'assets', employee.id], queryFn: () => listEmployeeAssets(employee.id) });
  const outstanding = (assetsQuery.data?.assets ?? []).filter((a) => a.current).length;

  const terminate = useMutation({
    mutationFn: () => terminateEmployee(employee.id, { terminationDate: date, reason: reason as TerminationReason, note: note.trim() || undefined }),
    onSuccess: (r) => setResult(r), // show the settlement before closing, rather than close immediately
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not terminate the employee.'),
  });

  const canSubmit = date !== '' && reason !== '' && !terminate.isPending;

  // Success — show the computed settlement (the point of surfacing it in the
  // response), then Done closes + refreshes the page.
  if (result) {
    const { payout, advanceDisposition: ad } = result.settlement;
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="terminate-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="w-full max-w-lg rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
          <header className="space-y-1">
            <h2 id="terminate-title" className="font-serif text-xl text-white">{employee.firstName} {employee.lastName} terminated</h2>
            <p className="text-sm text-gray-400">Recorded as of {formatDate(date)}. Final settlement below.</p>
          </header>
          <div className="rounded border border-gray-800 bg-gray-900/40 p-4 text-sm space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Termination payout</div>
              {payout ? (
                <p className="text-white">{formatMoney(payout.amount, payout.currency as CurrencyCode)}
                  <span className="text-gray-400"> — {payout.unusedAnnualDays.toFixed(2)} days’ unused annual leave encashed</span>
                </p>
              ) : (
                <p className="text-gray-400">No accrued annual leave to encash.</p>
              )}
            </div>
            {ad ? (
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Outstanding advances</div>
                <p className="text-white">{ad.advanceCount} advance{ad.advanceCount === 1 ? '' : 's'} — {ad.disposition === 'forgive' ? 'forgiven' : 'netted against the final settlement'}.</p>
              </div>
            ) : null}
            {payout ? <p className="text-xs text-gray-500">Run a final-settlement payroll for {formatDate(date)} to pay the payout.</p> : null}
            {outstanding > 0 ? <p className="text-xs text-yellow-300">⚠ {outstanding} company asset{outstanding === 1 ? '' : 's'} still to be returned.</p> : null}
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onDone} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="terminate-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
        <header className="space-y-1">
          <h2 id="terminate-title" className="font-serif text-xl text-white">Terminate {employee.firstName} {employee.lastName}</h2>
          <p className="text-sm text-gray-400">This records the termination and computes the final settlement. It cannot be undone.</p>
        </header>

        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (canSubmit) { setError(null); terminate.mutate(); } }}>
          <label className="block text-sm text-gray-300 space-y-1">
            <span>Termination date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Termination date" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
          </label>
          <label className="block text-sm text-gray-300 space-y-1">
            <span>Reason</span>
            <select value={reason} onChange={(e) => setReason(e.target.value as TerminationReason)} aria-label="Reason" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
              <option value="">Select a reason…</option>
              {(Object.keys(TERMINATION_REASON_LABELS) as TerminationReason[]).map((r) => <option key={r} value={r}>{TERMINATION_REASON_LABELS[r]}</option>)}
            </select>
          </label>
          <label className="block text-sm text-gray-300 space-y-1">
            <span>Note <span className="text-gray-500">(optional)</span></span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" rows={2} maxLength={500} className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
          </label>

          <div className="rounded border border-gray-800 bg-gray-900/40 p-3 text-xs text-gray-400 space-y-1">
            <p className="text-gray-300">What happens on termination:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>The record becomes <span className="text-gray-300">immutable</span> — to rehire, create a new employee.</li>
              <li>Unused annual leave is <span className="text-gray-300">encashed</span> into a termination payout — run a final-settlement payroll for {date || 'the termination date'} to pay it.</li>
              <li>Outstanding loans/advances are settled per your organisation's termination policy.</li>
              <li>Any future-dated leave is cancelled.</li>
            </ul>
            {outstanding > 0 ? (
              <p className="text-yellow-300 pt-1">⚠ {outstanding} company asset{outstanding === 1 ? '' : 's'} still in their custody — <span className="text-gray-300">not auto-returned</span>. Chase the return; termination proceeds regardless.</p>
            ) : null}
          </div>

          {error ? <Alert variant="error">{error}</Alert> : null}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} disabled={terminate.isPending} className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={!canSubmit} className="rounded bg-red-700 text-white px-4 py-2 text-sm font-medium hover:bg-red-600 disabled:opacity-50">
              {terminate.isPending ? 'Terminating…' : 'Terminate employment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
